export enum CubeType {
	"1D" = "1D",
	"3D" = "3D",
}

export interface CubeLookupTable {
	data: number[]
	domain: number[][]
	size: number
	title: string
	type: CubeType
}

export class CubeLutParser {
	static readonly MAX_LUT_SIZE = 32

	static isSupported(name: string): boolean {
		const extensions = ["cube", "vf", "3dl"]

		name = name.toLowerCase()
		const type = name.split(".").pop()
		if (!type) {
			return false
		}

		return extensions.includes(type)
	}

	static parseLutFile(content: string, extension: string): CubeLookupTable | null {
		if (extension === "3dl") {
			return this.parse3dl(content)
		} else if (extension === "cube" || extension === "vf") {
			return this.parseCube(content)
		} else {
			return null
		}
	}

	/**
	 * @param content .cube file format info:
	 *  * {@link https://forum.blackmagicdesign.com/viewtopic.php?f=21&t=40284}
	 *  * {@link http://archive.is/cWkgi}
	 */
	private static parseCubeLUT(content: string): CubeLookupTable {
		const parsedLUT: CubeLookupTable = {
			data: [],
			domain: [
				[0.0, 0.0, 0.0],
				[1.0, 1.0, 1.0],
			],
			size: 0,
			title: "Cube LUT",
			type: CubeType["3D"],
		}

		const lines = content.split("\n")

		for (let line of lines) {
			line = line.trim()

			if (line[0] === "#" || line === "") {
				// Skip comments and empty lines
				continue
			}

			const parts = line.split(/\s+/)

			switch (parts[0]) {
				case "TITLE":
					parsedLUT.title = line.slice(7, -1)
					break
				case "DOMAIN_MIN":
					parsedLUT.domain[0] = parts.slice(1).map(Number)
					break
				case "DOMAIN_MAX":
					parsedLUT.domain[1] = parts.slice(1).map(Number)
					break
				case "LUT_1D_SIZE":
					parsedLUT.type = CubeType["1D"]
					parsedLUT.size = Number(parts[1])
					break
				case "LUT_3D_SIZE":
					parsedLUT.type = CubeType["3D"]
					parsedLUT.size = Number(parts[1])
					break
				case "LUT_1D_INPUT_RANGE":
					// LUT_1D_INPUT_RANGE MIN MAX (where MIN is the minimum input value expected to be
					// passed through the 1D LUT. Like wise for MAX.)
					// Ignore this for now.
					break
				case "LUT_3D_INPUT_RANGE":
					// LUT_3D_INPUT_RANGE MIN MAX (where MIN is the minimum input value expected to be
					// passed through the 3D LUT. Like wise for MAX.)
					// Ignore this for now.
					break
				default:
					// data.push(parts.map(Number));
					parsedLUT.data.push(...parts.map(Number))
			}
		}

		if (!parsedLUT.size) {
			if (parsedLUT.type === CubeType["1D"]) {
				parsedLUT.size = Math.floor(parsedLUT.data.length / 3)
			} else {
				parsedLUT.size = Math.floor(Math.cbrt(parsedLUT.data.length / 3))
			}
		}

		const reorderedLUTData = new Array(parsedLUT.data.length)
		let index = 0

		for (let b = 0; b < parsedLUT.size; b++) {
			for (let g = 0; g < parsedLUT.size; g++) {
				for (let r = 0; r < parsedLUT.size; r++) {
					const oldIndex =
						(r * parsedLUT.size * parsedLUT.size + g * parsedLUT.size + b) * 3
					reorderedLUTData[index++] = parsedLUT.data[oldIndex + 0] // R
					reorderedLUTData[index++] = parsedLUT.data[oldIndex + 1] // G
					reorderedLUTData[index++] = parsedLUT.data[oldIndex + 2] // B
				}
			}
		}

		parsedLUT.data = reorderedLUTData

		return parsedLUT
	}

	static parseCube(data: string): CubeLookupTable {
		const lut = this.parseCubeLUT(data)
		this.convert1dCubeTo3d(lut)
		this.downscaleToMaxEdgeLength(lut)
		return lut
	}

	/* tslint:disable */
	/**
	 * Information about the 3DL file format:
	 * {@link http://download.autodesk.com/us/systemdocs/help/2009/lustre_ext1/files/WSc4e151a45a3b785a24c3d9a411df9298473-7ffd.htm}
	 * {@link https://thefoundry.s3.amazonaws.com/products/nuke/releases/9.0v1/NukeReferenceGuide.pdf}
	 */
	/* tslint:enable */
	static parse3dl(data: string): CubeLookupTable | null {
		const result: CubeLookupTable = {
			data: [],
			domain: [
				[0, 0, 0],
				[1, 1, 1],
			],
			size: 0,
			title: "3D LUT",
			type: CubeType["3D"],
		}
		let maxValue = 4095
		const lr = lineReader(data)
		let line: IteratorResult<string, void>
		for (line = lr.next(); line; line = lr.next()) {
			if (line.done) {
				return null
			}

			const lineParts = line.value.split(/ +/)
			if (Number.isFinite(Number(lineParts[0]))) {
				break
			} else {
				if (lineParts[0] === "MESH") {
					const meshMaxValue = Number(lineParts[2])
					if (Number.isFinite(meshMaxValue)) {
						maxValue = Math.pow(2, meshMaxValue) - 1
					}
				}
			}
		}
		if (line.done) {
			return null
		}
		let axisSubdivisionValues = line.value.split(/ +/).map(Number)
		if (axisSubdivisionValues.length === 1) {
			axisSubdivisionValues[1] = axisSubdivisionValues[0] ?? 0
		}

		/*
      NOTE(amiller): The 3DL format provides for nonlinear values in the input color space, but there isn't a spec for how
      they work and I don't have any examples of nonlinear input values. Assuming a linear input color space.
       */
		axisSubdivisionValues = [0, maxValue]

		for (const row of lr) {
			const rowValues = row.split(/ +/).map(Number).reverse()
			for (const c of rowValues) {
				const col = rowValues[c]

				if (!col) {
					continue
				}

				let value = 0
				for (let i = 0; i < axisSubdivisionValues.length + 1; i++) {
					const curr = axisSubdivisionValues[i]
					const next = axisSubdivisionValues[i + 1]

					if (!curr || !next) {
						continue
					}

					if (col >= curr && col <= next) {
						const difference = next - curr
						if (difference) {
							const fraction = (col - curr) / difference
							value = (i + fraction) / (axisSubdivisionValues.length - 1)
						} else {
							value = curr
						}
					}
				}
				rowValues[c] = value
			}
			result.data.push(...rowValues.map(Number))
		}
		result.size = Math.cbrt(result.data.length / 3)
		this.downscaleToMaxEdgeLength(result)
		return result
	}

	private static convert1dCubeTo3d = (lut: CubeLookupTable): void => {
		if (lut.type === CubeType["1D"]) {
			const data3d: number[] = []
			const inCoords = [0, 0, 0]
			const outCoords = [0, 0, 0]
			for (inCoords[0] = 0; inCoords[0] < lut.size; inCoords[0]++) {
				outCoords[0] = lut.data[inCoords[0]] ?? 0
				for (inCoords[1] = 0; inCoords[1] < lut.size; inCoords[1]++) {
					outCoords[1] = lut.data[lut.size + inCoords[1]] ?? 0
					for (inCoords[2] = 0; inCoords[2] < lut.size; inCoords[2]++) {
						outCoords[2] = lut.data[lut.size * 2 + inCoords[2]] ?? 0
						data3d.push(...outCoords)
					}
				}
			}
			lut.type = CubeType["3D"]
		}
	}

	private static lutCoord3Dto1D(data: number[], lutSize: number, coords3d: number[]): number[] {
		let outCoord1d = 0
		for (let i = 0; i < 3; i++) {
			const coord = coords3d[i]
			if (coord === undefined || !Number.isFinite(coord)) {
				throw new Error("Invalid LUT coordinate")
			}
			outCoord1d = outCoord1d * lutSize + coord
		}
		outCoord1d *= 3
		return data.slice(outCoord1d, outCoord1d + 3)
	}

	private static downscaleToMaxEdgeLength = (lut: CubeLookupTable): void => {
		if (lut.size <= CubeLutParser.MAX_LUT_SIZE) {
			return
		}
		const outData: number[] = []
		const inCoords = [0, 0, 0]
		for (inCoords[0] = 0; inCoords[0] < CubeLutParser.MAX_LUT_SIZE; inCoords[0]++) {
			for (inCoords[1] = 0; inCoords[1] < CubeLutParser.MAX_LUT_SIZE; inCoords[1]++) {
				for (inCoords[2] = 0; inCoords[2] < CubeLutParser.MAX_LUT_SIZE; inCoords[2]++) {
					const mapCoords = inCoords.map(
						(c) => (c * (lut.size - 1)) / (CubeLutParser.MAX_LUT_SIZE - 1),
					)
					const mapCell = [mapCoords.map(Math.floor), mapCoords.map(Math.ceil)]
					const mapCorners: [number, number, number] = [0, 0, 0]
					const currentCornerWeightComponents = [0, 0, 0]
					let weightedOutValue = [0, 0, 0]

					for (
						// @ts-expect-error nested value subscript
						mapCorners[0] = mapCell[0][0];
						// @ts-expect-error nested value subscript
						mapCorners[0] <= mapCell[1][0];
						mapCorners[0]++
					) {
						currentCornerWeightComponents[0] =
							// @ts-expect-error nested value subscript
							1 - Math.abs(mapCorners[0] - mapCoords[0])

						for (
							// @ts-expect-error nested value subscript
							mapCorners[1] = mapCell[0][1];
							// @ts-expect-error nested value subscript
							mapCorners[1] <= mapCell[1][1];
							mapCorners[1]++
						) {
							currentCornerWeightComponents[1] =
								// @ts-expect-error nested value subscript
								1 - Math.abs(mapCorners[1] - mapCoords[1])

							for (
								// @ts-expect-error nested value subscript
								mapCorners[2] = mapCell[0][2];
								// @ts-expect-error nested value subscript
								mapCorners[2] <= mapCell[1][2];
								mapCorners[2]++
							) {
								currentCornerWeightComponents[2] =
									// @ts-expect-error nested value subscript
									1 - Math.abs(mapCorners[2] - mapCoords[2])

								const currentCornerWeight = currentCornerWeightComponents.reduce(
									(prev, curr) => prev * curr,
									1,
								)
								const currentCornerOut = CubeLutParser.lutCoord3Dto1D(
									lut.data,
									lut.size,
									mapCorners,
								)
								weightedOutValue = weightedOutValue.map(
									// @ts-expect-error nested value subscript
									(c, i) => c + currentCornerWeight * currentCornerOut[i],
								)
							}
						}
					}
					outData.push(...weightedOutValue)
				}
			}
		}
		lut.size = CubeLutParser.MAX_LUT_SIZE
		lut.data = outData
	}
}

function* lineReader(data: string) {
	const lines = data.split("\n")
	for (let line of lines) {
		line = line.trim()
		if (line[0] === "#" || !line.length) {
			continue
		}
		yield line
	}
}
